"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { HiDotsHorizontal } from "react-icons/hi";
import { EMAIL_TOKENS } from "@/components/email-composer/styles/tokens";
import { Button } from "@/components/ui";
import { ResolvedTheme, useTheme } from "@/contexts/theme-provider";
import { cx } from "@/lib/tailwind-utils";
import { CustomTheme, presetThemes, rgbToHex } from "@/lib/themes";
import { AttachmentUrlInfo, EmailDirection, type EmailMessageType } from "@/types/email";
import { type EmailRenderContext, buildNormalizationCss } from "./email-content-normalizations";
interface EmailBodyRendererProps {
  bodyHtml: string | null;
  bodyText: string | null;
  className?: string;
  /** Map of content_id -> attachment URL info for replacing cid: references */
  inlineImageUrls?: Record<string, AttachmentUrlInfo>;
  /** Whether inline image URLs are currently loading */
  isLoadingInlineImages?: boolean;
  /** Direction of the message — drives outbound-only normalizations. */
  direction?: EmailDirection;
  messageType?: EmailMessageType;
  /**
   * Per-message light/dark pin from the header toggle (owned by
   * EmailMessageItem via `useEmailThemeToggle`). `null` follows the app theme;
   * otherwise it overrides the surface for this email only.
   */
  themeOverride?: ResolvedTheme | null;
}
interface SplitContent {
  main: string;
  quoted: string | null;
}

/**
 * Replaces cid: references in HTML with actual signed URLs.
 * cid:abc123 -> actual GCS signed URL
 */
function replaceCidReferences(html: string, inlineImageUrls?: Record<string, AttachmentUrlInfo>): string {
  if (!inlineImageUrls || Object.keys(inlineImageUrls).length === 0) {
    return html;
  }

  // Replace cid:xxx references with actual URLs
  // Pattern matches: src="cid:xxx" or src='cid:xxx'
  return html.replace(/src=["']cid:([^"']+)["']/gi, (match, contentId) => {
    const urlInfo = inlineImageUrls[contentId];
    if (urlInfo?.signedUrl) {
      return `src="${urlInfo.signedUrl}"`;
    }
    // Keep original if no URL found
    return match;
  });
}

/**
 * Detects and splits quoted content from email HTML.
 * Looks for common patterns like Gmail quotes, Outlook headers, "On ... wrote:" patterns.
 */
function splitQuotedHtml(html: string): SplitContent {
  const patterns = [
  // Gmail-style quote divs
  /(<div[^>]*class="[^"]*gmail_quote[^"]*"[^>]*>[\s\S]*$)/i,
  // Outlook-style quote markers
  /(<div[^>]*(?:id="divRplyFwdMsg"|class="[^"]*OutlookMessageHeader[^"]*")[^>]*>[\s\S]*$)/i,
  // "On [date] ... wrote:" pattern
  /(On\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[^<]*?wrote:[\s\S]*$)/i,
  // Blockquote elements
  /(<blockquote[^>]*>[\s\S]*$)/i];
  for (const pattern of patterns) {
    const match = html.match(pattern);
    if (match) {
      const quoteStart = html.indexOf(match[1]);
      return {
        main: html.substring(0, quoteStart).trim(),
        quoted: match[1]
      };
    }
  }
  return {
    main: html,
    quoted: null
  };
}

/**
 * Detects and splits quoted content from plain text email.
 */
function splitQuotedText(text: string): SplitContent {
  // Try "On [date] ... wrote:" pattern
  const wroteMatch = text.match(/(On\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)[^\n]*?wrote:[\s\S]*$)/i);
  if (wroteMatch) {
    const quoteStart = text.indexOf(wroteMatch[1]);
    return {
      main: text.substring(0, quoteStart).trim(),
      quoted: wroteMatch[1]
    };
  }

  // Try lines starting with ">"
  const lines = text.split("\n");
  let quoteStartIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim().startsWith(">")) {
      if (quoteStartIndex === -1) quoteStartIndex = i;
    } else if (quoteStartIndex !== -1 && lines[i].trim() !== "") {
      quoteStartIndex = -1;
    }
  }
  if (quoteStartIndex !== -1 && quoteStartIndex > 0) {
    return {
      main: lines.slice(0, quoteStartIndex).join("\n").trim(),
      quoted: lines.slice(quoteStartIndex).join("\n")
    };
  }
  return {
    main: text,
    quoted: null
  };
}
function isCustomTheme(theme: CustomTheme | ResolvedTheme | null): theme is CustomTheme {
  return theme !== null && typeof theme === "object" && "baseMode" in theme && "colors" in theme;
}

/** Resolve the email surface's dark/light mode and background color. */
function resolveEmailSurface(theme: CustomTheme | ResolvedTheme | null): {
  isDark: boolean;
  backgroundColor: string;
} {
  const isDark = isCustomTheme(theme) ? theme.baseMode === "dark" : theme === "dark";
  const backgroundColor = isCustomTheme(theme) ? rgbToHex(theme.colors.background) : rgbToHex(isDark ? presetThemes[1].colors.background : presetThemes[0].colors.background);
  return {
    isDark,
    backgroundColor
  };
}

/**
 * Build the sandboxed-iframe document: a minimal base stylesheet plus the CSS
 * contributed by every applicable content normalization (dark-mode invert,
 * white neutralization, outbound full-bleed, …). See
 * `email-content-normalizations` to add or change a transform.
 */
function prepareHtmlContent(html: string, context: EmailRenderContext, backgroundColor: string): string {
  const normalizationCss = buildNormalizationCss(context);

  // Default the body typography for unstyled content (our conversational sends,
  // plain inbound notes) to the same values our own outgoing mail carries inline
  // — Arial, 14px, #1a1a1a (DEFAULT_STYLE_SETTINGS) — so what's read matches what's
  // sent instead of falling back to the UA serif at 16px/pure black. Outgoing mail
  // sets no line-height, so 1.5 here is purely a readability default (UA ~1.2 is
  // cramped, and the plain-text path already uses relaxed leading). Emails that
  // carry their own inline/embedded styles still override all of this.
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank"><style>
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: ${backgroundColor}; font-family: ${EMAIL_TOKENS.fontFamily.sans}; font-size: ${EMAIL_TOKENS.fontSize.sm}; line-height: ${EMAIL_TOKENS.lineHeight.normal}; color: ${EMAIL_TOKENS.colors.text.primary}; }
body { display: flow-root; }
img { max-width: 100%; height: auto; }
${normalizationCss}
</style></head><body>${html}</body></html>`;
}
function SandboxedIframe({
  html,
  title,
  context,
  backgroundColor
}: {
  html: string;
  title: string;
  context: EmailRenderContext;
  backgroundColor: string;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(100);
  const srcDoc = useMemo(() => prepareHtmlContent(html, context, backgroundColor), [html, context, backgroundColor]);
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const handleLoad = () => {
      try {
        const doc = iframe.contentDocument;
        if (doc?.body) {
          setHeight(Math.max(doc.body.scrollHeight, 50));
        }
      } catch {
        setHeight(200);
      }
    };
    iframe.addEventListener("load", handleLoad);
    return () => iframe.removeEventListener("load", handleLoad);
  }, [srcDoc]);
  return <iframe ref={iframeRef} srcDoc={srcDoc} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" title={title} className="w-full border-0" style={{
    height: `${height}px`,
    minHeight: "50px"
  }} />;
}
function QuotedToggleButton(t0) {
  const $ = _c(4);
  const {
    showQuoted,
    onToggle
  } = t0;
  const t1 = showQuoted ? "Hide quoted content" : "Show quoted content";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <HiDotsHorizontal className="py-0" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== onToggle || $[2] !== t1) {
    t3 = <Button onClick={onToggle} variant="ghost" size="sm" tooltip={t1} className="my-1 md:!py-0" icon={t2} />;
    $[1] = onToggle;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}

/**
 * Renders email content safely using a sandboxed iframe.
 * Collapses quoted replies (Gmail-style) by default.
 */
export function EmailBodyRenderer(t0) {
  const $ = _c(55);
  const {
    bodyHtml,
    bodyText,
    className,
    inlineImageUrls,
    isLoadingInlineImages,
    direction,
    messageType,
    themeOverride
  } = t0;
  const [showQuoted, setShowQuoted] = useState(false);
  const {
    customTheme,
    resolvedTheme
  } = useTheme();
  const emailTheme = themeOverride ?? customTheme ?? resolvedTheme;
  let t1;
  bb0: {
    if (!bodyHtml) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== bodyHtml || $[1] !== inlineImageUrls) {
      t2 = replaceCidReferences(bodyHtml, inlineImageUrls);
      $[0] = bodyHtml;
      $[1] = inlineImageUrls;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const processedBodyHtml = t1;
  let t2;
  bb1: {
    if (processedBodyHtml) {
      let t3;
      if ($[3] !== processedBodyHtml) {
        t3 = splitQuotedHtml(processedBodyHtml);
        $[3] = processedBodyHtml;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      t2 = t3;
      break bb1;
    }
    if (bodyText) {
      let t3;
      if ($[5] !== bodyText) {
        t3 = splitQuotedText(bodyText);
        $[5] = bodyText;
        $[6] = t3;
      } else {
        t3 = $[6];
      }
      t2 = t3;
      break bb1;
    }
    let t3;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        main: "",
        quoted: null
      };
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = t3;
  }
  const splitContent = t2;
  let t3;
  if ($[8] !== emailTheme) {
    t3 = resolveEmailSurface(emailTheme);
    $[8] = emailTheme;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    isDark,
    backgroundColor
  } = t3;
  let t4;
  if ($[10] !== direction || $[11] !== isDark || $[12] !== messageType) {
    t4 = {
      isDark,
      direction,
      messageType
    };
    $[10] = direction;
    $[11] = isDark;
    $[12] = messageType;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const renderContext = t4;
  if (isLoadingInlineImages && bodyHtml) {
    let t5;
    if ($[14] !== className) {
      t5 = cx("relative", className);
      $[14] = className;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="space-y-3 p-4 animate-pulse"><div className="h-4 bg-background-secondary rounded w-3/4" /><div className="h-4 bg-background-secondary rounded w-full" /><div className="h-4 bg-background-secondary rounded w-5/6" /><div className="h-4 bg-background-secondary rounded w-2/3" /><div className="h-32 bg-background-secondary rounded w-48 mt-4" /><div className="h-4 bg-background-secondary rounded w-full" /><div className="h-4 bg-background-secondary rounded w-4/5" /></div>;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    let t7;
    if ($[17] !== t5) {
      t7 = <div className={t5}>{t6}</div>;
      $[17] = t5;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    return t7;
  }
  const hasQuotedContent = splitContent.quoted !== null;
  if (processedBodyHtml) {
    let t5;
    if ($[19] !== backgroundColor || $[20] !== renderContext || $[21] !== splitContent.main) {
      t5 = <SandboxedIframe html={splitContent.main} title="Email content" context={renderContext} backgroundColor={backgroundColor} />;
      $[19] = backgroundColor;
      $[20] = renderContext;
      $[21] = splitContent.main;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    let t6;
    if ($[23] !== hasQuotedContent || $[24] !== showQuoted) {
      t6 = hasQuotedContent && <QuotedToggleButton showQuoted={showQuoted} onToggle={() => setShowQuoted(!showQuoted)} />;
      $[23] = hasQuotedContent;
      $[24] = showQuoted;
      $[25] = t6;
    } else {
      t6 = $[25];
    }
    let t7;
    if ($[26] !== t6) {
      t7 = <div className="flex items-center gap-1">{t6}</div>;
      $[26] = t6;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    let t8;
    if ($[28] !== backgroundColor || $[29] !== hasQuotedContent || $[30] !== renderContext || $[31] !== showQuoted || $[32] !== splitContent.quoted) {
      t8 = hasQuotedContent && showQuoted && <div className="mt-2 pl-3 border-l-2 border-border"><SandboxedIframe html={splitContent.quoted} title="Quoted email content" context={renderContext} backgroundColor={backgroundColor} /></div>;
      $[28] = backgroundColor;
      $[29] = hasQuotedContent;
      $[30] = renderContext;
      $[31] = showQuoted;
      $[32] = splitContent.quoted;
      $[33] = t8;
    } else {
      t8 = $[33];
    }
    let t9;
    if ($[34] !== className || $[35] !== t5 || $[36] !== t7 || $[37] !== t8) {
      t9 = <div className={className}>{t5}{t7}{t8}</div>;
      $[34] = className;
      $[35] = t5;
      $[36] = t7;
      $[37] = t8;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    return t9;
  }
  if (bodyText) {
    let t5;
    if ($[39] !== className) {
      t5 = cx("relative", className);
      $[39] = className;
      $[40] = t5;
    } else {
      t5 = $[40];
    }
    let t6;
    if ($[41] !== splitContent.main) {
      t6 = <div className="whitespace-pre-wrap text-body text-foreground leading-relaxed">{splitContent.main}</div>;
      $[41] = splitContent.main;
      $[42] = t6;
    } else {
      t6 = $[42];
    }
    let t7;
    if ($[43] !== hasQuotedContent || $[44] !== showQuoted || $[45] !== splitContent.quoted) {
      t7 = hasQuotedContent && <div><QuotedToggleButton showQuoted={showQuoted} onToggle={() => setShowQuoted(!showQuoted)} />{showQuoted && <div className="pl-3 border-l-2 border-border whitespace-pre-wrap text-body text-foreground-secondary leading-relaxed">{splitContent.quoted}</div>}</div>;
      $[43] = hasQuotedContent;
      $[44] = showQuoted;
      $[45] = splitContent.quoted;
      $[46] = t7;
    } else {
      t7 = $[46];
    }
    let t8;
    if ($[47] !== t5 || $[48] !== t6 || $[49] !== t7) {
      t8 = <div className={t5}>{t6}{t7}</div>;
      $[47] = t5;
      $[48] = t6;
      $[49] = t7;
      $[50] = t8;
    } else {
      t8 = $[50];
    }
    return t8;
  }
  let t5;
  if ($[51] !== className) {
    t5 = cx("text-body text-foreground-muted italic", className);
    $[51] = className;
    $[52] = t5;
  } else {
    t5 = $[52];
  }
  let t6;
  if ($[53] !== t5) {
    t6 = <div className={t5}>No content available</div>;
    $[53] = t5;
    $[54] = t6;
  } else {
    t6 = $[54];
  }
  return t6;
}